import Image from 'next/image'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useState } from 'react'

import camelcase from 'camelcase'
import { NextSeo } from 'next-seo'

import { Box, Container, Grid } from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import { Pagination } from '@material-ui/lab'

import BlogPhotoCard from '~/components/cards/BlogPhotoCard'
import AppLayout from '~/components/layouts/AppLayout'
import SubscribeSection from '~/components/sections/SubscribeSection'
import Flexbox from '~/components/shared/Flexbox'
import LoaderBox from '~/components/shared/LoaderBox'
import ReactHtmlParser from '~/components/shared/ReactHtmlParser'
import Section from '~/components/shared/Section'
import Text, { Title } from '~/components/shared/Text'
import { ReCaptchaProvider } from '~/containers/ReCaptchaProvider'
import BlogFeaturedSlider from '~/views/Blog/BlogFeaturedSlider/BlogFeaturedSlider'
import BlogHeader from '~/views/Blog/BlogHeader'

import { useGetSearchArticles } from '~/services/Query/news-reviews/news-review.query'

import { Theme } from '~/theme/theme'

import { formatMessage, getEntityByBrand } from '~/brands'
import { IS_BROWSER } from '~/constants'

import bgImageBoat from '~/../public/static/images/blog-hero-category.boat.jpg'
import bgImageRv from '~/../public/static/images/blog-hero-category.rv.jpg'

const bgImage = getEntityByBrand<string>({ rv: bgImageRv, boat: bgImageBoat })

const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles({
      hero: {
        position: 'relative',
        minWidth: '375px',
        zIndex: 0,
        padding: `${theme.spacing(8)}px 0`,
        '& img.blog-hero-image': {
          position: 'absolute',
          zIndex: -1,
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'bottom'
        }
      },
      subTitle: {
        color: theme.palette.common.white
      }
    }),
  { name: 'BlogCategory' }
)

const PAGE_SIZE = 12

export default function BlogCategory() {
  const classes = useStyles()
  const router = useRouter()
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(+router.query.page || 1)
  }, [router.query.page])

  const {
    data: featuredArticles,
    isFetching: featuredIsValidating
  } = useGetSearchArticles({
    params: {
      byCategory: router.query?.category as string,
      limit: 3,
      featured: true
    }
  })

  const { data, error, isLoading } = useGetSearchArticles({
    params: {
      byCategory: router.query?.category as string,
      page: +router.query.page || 1,
      limit: PAGE_SIZE
    }
  })

  const { articles, pagy } = data || {}

  const firstLoading = !articles?.length && isLoading && !error

  const handleChangePage = (_e, newPage) => {
    if (IS_BROWSER) {
      window.scrollTo({ top: 0 })
    }
    router.push({ query: { ...router.query, page: newPage } })
  }

  const sliderItems = useMemo(
    () =>
      featuredArticles.articles.map((item) => ({
        id: item.id,
        name: item.page_title,
        image: item.primary_image_url,
        category: router.query?.category as string
      })),
    [featuredArticles]
  )

  const categoryData = useMemo(() => {
    const currentCategory =
      formatMessage(`blog.${camelcase(router.query?.category || '')}`) || {}

    return {
      title: router.query?.category as string,
      description: '',
      ...currentCategory
    }
  }, [router.query?.category])

  return (
    <AppLayout
      disableGutters
      containerProps={{ disableGutters: true, maxWidth: false }}
    >
      <NextSeo title={router.query?.category as string} />
      <NextSeo
        title={router.query?.category as string}
        description={formatMessage('reviewsNewsPage.meta.description')}
        openGraph={{
          title: router.query?.category as string,
          description: formatMessage('reviewsNewsPage.meta.description')
        }}
      />

      <BlogHeader />
      <Box className={classes.hero}>
        <Container maxWidth="lg">
          <Image
            className="blog-hero-image"
            src={bgImage}
            placeholder="blur"
            layout="fill"
            objectFit="cover"
            objectPosition="center"
            alt="blog-hero"
          />
          <Container maxWidth="md">
            <Title
              mx="auto"
              variant="h1"
              textAlign="center"
              style={{ color: 'white' }}
            >
              {categoryData.title}
            </Title>
            {categoryData.description && (
              <Text style={{ color: 'white' }}>
                <ReactHtmlParser data={categoryData.description} />
              </Text>
            )}
          </Container>
        </Container>
      </Box>
      <BlogFeaturedSlider
        data={sliderItems}
        loading={featuredIsValidating && !sliderItems.length}
      />
      <Section title={categoryData.title}>
        <Grid container spacing={3} justify="center">
          <LoaderBox loading={firstLoading} length={articles?.length}>
            {articles?.map((item) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                <BlogPhotoCard data={item} loading={isLoading} />
              </Grid>
            ))}
          </LoaderBox>
        </Grid>
        {pagy.count > PAGE_SIZE && !!articles?.length && (
          <Flexbox mx="auto" justifyContent="center" mt={4}>
            <Pagination
              count={Math.ceil(pagy.count / PAGE_SIZE)}
              color="primary"
              shape="rounded"
              size="medium"
              onChange={handleChangePage}
              page={page}
            />
          </Flexbox>
        )}
      </Section>

      <ReCaptchaProvider>
        <SubscribeSection />
      </ReCaptchaProvider>
    </AppLayout>
  )
}
